// src/sim/lighting/lights.ts — motion-light state machine (VIS-05, D-05).
//
// Invariants:
//   - Pure function: returns { next, events }; never mutates `prev`.
//   - Deterministic: tick-count timers only; no setTimeout/Date.now (ESLint enforces).
//   - Permanent lights are NOT tracked here — they're always-on, consumed directly by lightLevelAt.
//
// Source: .planning/phases/02-fov-lighting/02-RESEARCH.md §Pattern 3.

import type { Entity, LightsState } from '../../shared/types/world';
import type { LightAuth } from '../../shared/types/map';
import { P2_MOTION_LIGHT_FADE_TICKS, P2_MOTION_LIGHT_STAY_TICKS } from './constants';

/** Re-exports so downstream consumers (tick.ts, tests) can import from './lights' by convention. */
export { P2_MOTION_LIGHT_STAY_TICKS as MOTION_LIGHT_STAY_TICKS } from './constants';
export { P2_MOTION_LIGHT_FADE_TICKS as MOTION_LIGHT_FADE_TICKS } from './constants';

export type MotionLightEvent =
  | { type: 'motion-light-on'; lightId: string; tick: number }
  | { type: 'motion-light-off'; lightId: string; tick: number };

export function advanceMotionLights(
  prev: LightsState,
  lights: readonly LightAuth[],
  entities: readonly Entity[],
  tick: number,
  /**
   * Pixels-per-tile (from MapData.tileWidth). Converts the authored tile-space
   * triggerRadius to pixels before comparison with the entity-to-light squared
   * pixel distance. Fixed in 02-08 Gap 3 closure — symmetric with the
   * `radiusPx = L.radius * mapData.tileWidth` pattern already used by
   * src/sim/lighting/light-level.ts:45. Pass 0 to keep the legacy
   * mapData-less tick() path inert (no motion light can fire when tileWidth
   * is unknown, which is what mapData-less fixtures expect anyway — they
   * don't author `lights`).
   */
  tileWidth: number,
): { next: LightsState; events: MotionLightEvent[] } {
  const next: LightsState = { ...prev };
  const events: MotionLightEvent[] = [];

  for (const L of lights) {
    if (L.type !== 'motion') continue;
    const id = L.id;
    const state = prev[id];

    // 02-08 Gap 3 closure: triggerRadius is authored in TILE units (see
    // src/shared/types/map.ts:38 and 02-CONTEXT.md D-02). Entity + light
    // positions are WORLD PIXELS. Convert to pixels before the squared compare.
    // Mirror of light-level.ts:45 `radiusPx = L.radius * mapData.tileWidth`.
    const triggerRadiusPx = L.triggerRadius * tileWidth;
    const triggerRadiusPxSquared = triggerRadiusPx * triggerRadiusPx;

    const triggered = entities.some(
      (e) => squaredDistance(e.position, L.position) <= triggerRadiusPxSquared,
    );

    if (triggered) {
      // on OR re-arming during fade — event fires only when transitioning FROM fading/off.
      if (!state || state.fadingTick !== null) {
        events.push({ type: 'motion-light-on', lightId: id, tick });
      }
      next[id] = { activatedTick: tick, fadingTick: null };
    } else if (state && state.fadingTick === null) {
      // Was on; check if stay-on elapsed.
      if (tick - state.activatedTick >= P2_MOTION_LIGHT_STAY_TICKS) {
        next[id] = { activatedTick: state.activatedTick, fadingTick: tick };
      }
    } else if (state && state.fadingTick !== null) {
      // In fade; check completion.
      if (tick - state.fadingTick >= P2_MOTION_LIGHT_FADE_TICKS) {
        delete next[id];
        events.push({ type: 'motion-light-off', lightId: id, tick });
      }
    }
  }
  return { next, events };
}

function squaredDistance(a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}
